import { Component, HostListener } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { first, forkJoin } from 'rxjs';
import { AuthorizationService } from '../../services/authorization.service';
import { LoadingService } from '../../services/loading.service';
import { DashboardService } from '../../services/dashboard.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TrainingGroupService } from 'src/app/services/training-group.service';
import { PersonService } from 'src/app/services/person.service';
import { DatePeriodModel } from 'src/app/models/date-period.model';
import { CompetencyMatrixWidgetComponent } from 'src/app/pages/dashboard/widgets/competency-matrix-widget/competency-matrix-widget.component';
import { MyQualificationStatusWidgetComponent } from 'src/app/pages/dashboard/widgets/my-qualification-status-widget/my-qualification-status-widget.component';
import { PersonCompetencyMatrixWidgetComponent } from 'src/app/pages/dashboard/widgets/person-competency-matrix-widget/person-competency-matrix-widget.component';
import { QualificationStatusWidgetComponent } from 'src/app/pages/dashboard/widgets/qualification-status-widget/qualification-status-widget.component';
import { Top10RecordsWidgetComponent } from 'src/app/pages/dashboard/widgets/top-10-records-widget/top-10-records-widget.component';
import { TrainingGroupQualificationStatusWidgetComponent } from 'src/app/pages/dashboard/widgets/training-group-qualification-status-widget/training-group-qualification-status-widget.component';
import { ComplianceCourseQualificationStatusWidgetComponent } from 'src/app/pages/dashboard/widgets/compliance-course-qualification-status-widget/compliance-course-qualification-status-widget.component';
import { LearningPathStatusWidgetComponent } from 'src/app/pages/dashboard/widgets/learning-path-status-widget/learning-path-status-widget.component';
import { ReportingWidgetComponent } from 'src/app/pages/dashboard/widgets/reporting-widget/reporting-widget.component';
import { TrendToTargetIddWidgetComponent } from 'src/app/pages/dashboard/widgets/trend-to-target-idd-widget/trend-to-target-idd-widget.component';
import { DashboardWidgetsDataService } from 'src/app/services/dashboard-widget-data.service';
import { CurrentLearningPathStatusWidgetComponent } from 'src/app/pages/dashboard/widgets/current-learning-path-status-widget/current-learning-path-status-widget.component';
import { WidgetService } from 'src/app/services/widget.service';
import { WIDGET_KEYS } from 'src/app/constants/widget-keys.constants';
import { WidgetConfigModel } from 'src/app/models/widget/widget-config.model';
import { getDateRangeByPeriod } from 'src/app/utils/date-range.utils';
import { WidgetModel } from 'src/app/models/widget/widget.model';
import { PersonWidgetOrderModel } from 'src/app/models/widget/person-widget-order.model';
import { CompetencyMatrixWidgetDemoComponent } from './demo-widgets/competency-matrix-widget-demo/competency-matrix-widget-demo.component';
import { ComplianceCourseQualificationStatusWidgetDemoComponent } from './demo-widgets/compliance-course-qualification-status-widget-demo/compliance-course-qualification-status-widget-demo.component';
import { CurrentLearningPathStatusWidgetDemoComponent } from './demo-widgets/current-learning-path-status-widget-demo/current-learning-path-status-widget-demo.component';
import { LearningPathStatusWidgetDemoComponent } from './demo-widgets/learning-path-status-widget-demo/learning-path-status-widget-demo.component';
import { MyQualificationStatusWidgetDemoComponent } from './demo-widgets/my-qualification-status-widget-demo/my-qualification-status-widget-demo.component';
import { PersonCompetencyMatrixWidgetDemoComponent } from './demo-widgets/person-competency-matrix-widget-demo/person-competency-matrix-widget-demo.component';
import { QualificationStatusWidgetDemoComponent } from './demo-widgets/qualification-status-widget-demo/qualification-status-widget-demo.component';
import { Top10RecordsWidgetDemoComponent } from './demo-widgets/top-10-records-widget-demo/top-10-records-widget-demo.component';
import { TrainingGroupQualificationStatusWidgetDemoComponent } from './demo-widgets/training-group-qualification-status-widget-demo/training-group-qualification-status-widget-demo.component';
import { TrendToTargetIddWidgetDemoComponent } from './demo-widgets/trend-to-target-idd-widget-demo/trend-to-target-idd-widget-demo.component';
import { ReportingWidgetDemoComponent } from './demo-widgets/reporting-widget-demo/reporting-widget-demo.component';
import { GalleryWidgetConfigModel } from 'src/app/models/widget/gallery-widget-config.model';
import { calendarIcon } from 'src/app/app-icons';
import { ToastrService } from 'ngx-toastr';
import { ApprovalRequestService } from 'src/app/services/approval-request.service';
import { ChannelTrainingParticipantsWidgetComponent } from './widgets/channel-training-participants-widget/channel-training-participants-widget.component';
import { ChannelTrainingParticipantsWidgetDemoComponent } from './demo-widgets/channel-training-participants-widget-demo/channel-training-participants-widget-demo.component';
import { PendingRequestsWidgetDemoComponent } from './demo-widgets/pending-requests-widget-demo/pending-requests-widget-demo.component';
import { PendingRequestsWidgetComponent } from './widgets/pending-requests-widget/pending-requests-widget.component';
import { UserContextService } from 'src/app/services/user-context.service';
import { OrganisationUnitModel } from 'src/app/models/organisation-unit/organisation-unit.model';
import { OrganisationUnitService } from 'src/app/services/organisation-unit.service';
import { TenantContextService } from 'src/app/services/tenant-context.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false
})
export class DashboardComponent {
  isSuperAdmin: boolean = false;
  isManager: boolean = false;
  isTrainer: boolean = false;
  isEmployee: boolean = false;
  isBroker: boolean = false;
  isBrokerPoolTenant: boolean = false;

  qualificationStatusFilterYear: number | null = null;
  selectedPeriod: DatePeriodModel | null = null;
  customDateRange: (string | number | Date)[] = [null!, null!];

  widgetLoadingStatus: Map<string, boolean> = new Map<string, boolean>();

  isInitialDataLoaded = false;
  isDashboardStatisticsLoaded: boolean = false;
  isLearningRecommendationsLoaded: boolean = false;
  isPersonWidgetsLoaded: boolean = false;
  isVisibleWidgetsLoaded: boolean = false;

  isUserInIddGroup: boolean = false;
  isUserHasLearningPath: boolean = false;

  isCustomDatePopupOpen: boolean= false;
  isAddWidgetDrawerOpen: boolean = false;
  dragSource: 'gallery' | 'dashboard' | null = null;

  yearsDropdownDataSource: { id: number, name: string|number }[] = [
    {
      id: new Date().getFullYear(),
      name: new Date().getFullYear(),
    },
    {
      id: new Date().getFullYear() - 1,
      name: new Date().getFullYear() - 1,
    },
    {
      id: new Date().getFullYear() - 2,
      name: new Date().getFullYear() - 2,
    },
    {
      id: new Date().getFullYear() - 3,
      name: new Date().getFullYear() - 3,
    },
    {
      id: new Date().getFullYear() - 4,
      name: new Date().getFullYear() - 4,
    },
  ];

  periodPanelItems:any[] = [
    {
      text: this.translateService.instant('PERIODS.WEEK'),
      key: DatePeriodModel.ThisWeek
    }, {
      text: this.translateService.instant('PERIODS.MONTH'),
      key: DatePeriodModel.ThisMonth
    }, {
      text: this.translateService.instant('PERIODS.YEAR'),
      key: DatePeriodModel.ThisYear
    }, {
      text: this.translateService.instant('PERIODS.PREVIOUS-YEAR'),
      key: DatePeriodModel.PreviousYear
    }, {
      text: this.translateService.instant('PERIODS.CUSTOM-UPPERCASE'),
      key: DatePeriodModel.Custom
    }
  ];

  widgetNameToKeyMap: Record<string, string> = {
    'Top 10 Records': WIDGET_KEYS.TOP_10_RECORDS,
    'Qualification Status': WIDGET_KEYS.EMPLOYEES_PROGRESS,
    'My Qualification Status': WIDGET_KEYS.PERSON_PROGRESS,
    'Channel Training Participants': WIDGET_KEYS.CHANNEL_PROGRESS,
    'Competency Matrix': WIDGET_KEYS.COMPETENCY_MATRIX_DATA,
    'Person Competency Matrix': WIDGET_KEYS.PERSON_COMPETENCY_MATRIX_DATA,
    'Training Group Qualification': WIDGET_KEYS.TRAINING_GROUPS_PROGRESS,
    'Compliance Course Qualification': WIDGET_KEYS.COMPLIANCE_COURSES_PROGRESS,
    'Learning Path Status': WIDGET_KEYS.LEARNING_PATHS_PROGRESS,
    'Current Learning Path Status': WIDGET_KEYS.LEARNING_PATHS_PROGRESS_CURRENT,
    'Trend to Target IDD': WIDGET_KEYS.TREND_TO_TARGET_IDD_DATA,
    'Approval Requests': WIDGET_KEYS.PENDING_REQUESTS, //TODO rename widget name in db
    'Reporting': WIDGET_KEYS.REPORTING,
  };

  widgetKeyToComponentMap: Record<string, any> = {
    [WIDGET_KEYS.TOP_10_RECORDS]: Top10RecordsWidgetComponent,
    [WIDGET_KEYS.EMPLOYEES_PROGRESS]: QualificationStatusWidgetComponent,
    [WIDGET_KEYS.PERSON_PROGRESS]: MyQualificationStatusWidgetComponent,
    [WIDGET_KEYS.CHANNEL_PROGRESS]: ChannelTrainingParticipantsWidgetComponent,
    [WIDGET_KEYS.COMPETENCY_MATRIX_DATA]: CompetencyMatrixWidgetComponent,
    [WIDGET_KEYS.PERSON_COMPETENCY_MATRIX_DATA]: PersonCompetencyMatrixWidgetComponent,
    [WIDGET_KEYS.TRAINING_GROUPS_PROGRESS]: TrainingGroupQualificationStatusWidgetComponent,
    [WIDGET_KEYS.COMPLIANCE_COURSES_PROGRESS]: ComplianceCourseQualificationStatusWidgetComponent,
    [WIDGET_KEYS.LEARNING_PATHS_PROGRESS]: LearningPathStatusWidgetComponent,
    [WIDGET_KEYS.LEARNING_PATHS_PROGRESS_CURRENT]: CurrentLearningPathStatusWidgetComponent,
    [WIDGET_KEYS.TREND_TO_TARGET_IDD_DATA]: TrendToTargetIddWidgetComponent,
    [WIDGET_KEYS.PENDING_REQUESTS]: PendingRequestsWidgetComponent,
    [WIDGET_KEYS.REPORTING]: ReportingWidgetComponent,
  };

  widgetKeyToDemoComponentMap: Record<string, any> = {
    [WIDGET_KEYS.TOP_10_RECORDS]: Top10RecordsWidgetDemoComponent,
    [WIDGET_KEYS.EMPLOYEES_PROGRESS]: QualificationStatusWidgetDemoComponent,
    [WIDGET_KEYS.PERSON_PROGRESS]: MyQualificationStatusWidgetDemoComponent,
    [WIDGET_KEYS.CHANNEL_PROGRESS]: ChannelTrainingParticipantsWidgetDemoComponent,
    [WIDGET_KEYS.COMPETENCY_MATRIX_DATA]: CompetencyMatrixWidgetDemoComponent,
    [WIDGET_KEYS.PERSON_COMPETENCY_MATRIX_DATA]: PersonCompetencyMatrixWidgetDemoComponent,
    [WIDGET_KEYS.TRAINING_GROUPS_PROGRESS]: TrainingGroupQualificationStatusWidgetDemoComponent,
    [WIDGET_KEYS.COMPLIANCE_COURSES_PROGRESS]: ComplianceCourseQualificationStatusWidgetDemoComponent,
    [WIDGET_KEYS.LEARNING_PATHS_PROGRESS]: LearningPathStatusWidgetDemoComponent,
    [WIDGET_KEYS.LEARNING_PATHS_PROGRESS_CURRENT]: CurrentLearningPathStatusWidgetDemoComponent,
    [WIDGET_KEYS.TREND_TO_TARGET_IDD_DATA]: TrendToTargetIddWidgetDemoComponent,
    [WIDGET_KEYS.PENDING_REQUESTS]: PendingRequestsWidgetDemoComponent,
    [WIDGET_KEYS.REPORTING]: ReportingWidgetDemoComponent,
  };

  widgetKeyToTranslationKeyMap: Record<string, string> = {
    [WIDGET_KEYS.TOP_10_RECORDS]: 'WIDGETS.TOP-10-RECORDS',
    [WIDGET_KEYS.EMPLOYEES_PROGRESS]: 'WIDGETS.QUALIFICATION-STATUS',
    [WIDGET_KEYS.PERSON_PROGRESS]: 'WIDGETS.MY-QUALIFICATION-STATUS',
    [WIDGET_KEYS.CHANNEL_PROGRESS]: 'WIDGETS.CHANNEL-TRAINING-PARTICIPANTS',
    [WIDGET_KEYS.COMPETENCY_MATRIX_DATA]: 'WIDGETS.COMPETENCY-MATRIX',
    [WIDGET_KEYS.PERSON_COMPETENCY_MATRIX_DATA]: 'WIDGETS.PERSON-COMPETENCY-MATRIX',
    [WIDGET_KEYS.TRAINING_GROUPS_PROGRESS]: 'WIDGETS.TRAINING-GROUP-QUALIFICATION',
    [WIDGET_KEYS.COMPLIANCE_COURSES_PROGRESS]: 'WIDGETS.COMPLIANCE-COURSE-QUALIFICATION',
    [WIDGET_KEYS.LEARNING_PATHS_PROGRESS]: 'WIDGETS.LEARNING-PATH-STATUS',
    [WIDGET_KEYS.LEARNING_PATHS_PROGRESS_CURRENT]: 'WIDGETS.CURRENT-LEARNING-PATH-STATUS',
    [WIDGET_KEYS.TREND_TO_TARGET_IDD_DATA]: 'WIDGETS.TREND-TO-TARGET-IDD',
    [WIDGET_KEYS.PENDING_REQUESTS]: 'WIDGETS.IMPORTANT-TASKS',
    [WIDGET_KEYS.REPORTING]: 'WIDGETS.REPORTING',
  };

  dashboardWidgets: WidgetConfigModel[] = [];
  visibleWidgets: WidgetConfigModel[] = [];
  personWidgets: WidgetConfigModel[] = [];
  personWidgetKeys: string[] = [];

  editingWidgets: WidgetConfigModel[] = [];
  widgetsGallery: GalleryWidgetConfigModel[] = [];
  widgetColumns: WidgetConfigModel[][] = [[], []];

  selectedOrganisationUnitIds: string[] = [];
  organisationUnits: OrganisationUnitModel[] = [];

  editMode = false;
  
  DatePeriodModel = DatePeriodModel;
  WIDGET_KEYS = WIDGET_KEYS;

  minFilterDate!: Date;
  maxFilterDate!: Date;

  isMobile = false;

  get canViewLearningRecommendations(): boolean {
    return this.isEmployee || this.isManager || this.isTrainer || this.isBroker;
  }

  get isTreatedAsEmployee(): boolean {
    return this.isEmployee  ||
      (this.isBroker && this.isBrokerPoolTenant);
  }

  get canSeeEmployeeWidgets(): boolean {
    return !this.isUserInIddGroup &&
      this.isTreatedAsEmployee;
  }

  get hasVisiblePersonWidgets(): boolean {
    return this.personWidgets.some(widget => 
      this.getWidgetHasData(widget.key));
  }
    
  constructor(
    private sanitized: DomSanitizer,
    private loadingService: LoadingService,
    private dashboardService: DashboardService,
    private router: Router,
    private translateService: TranslateService,
    private trainingGroupService: TrainingGroupService,
    private personService: PersonService,
    private widgetsDataService: DashboardWidgetsDataService,
    private widgetService: WidgetService,
    private toastrService: ToastrService,
    private userContext: UserContextService,
    private tenantContext: TenantContextService,
    private organisationUnitService: OrganisationUnitService
  ) {

  }

  @HostListener('window:resize')
  onResize() {
    this.checkScreenWidth();
  }

  checkScreenWidth() {
    this.isMobile = window.innerWidth <= 480;
  }
  
  getCalendarIcon() {
    return this.sanitized.bypassSecurityTrustHtml(calendarIcon);
  }

  ngOnInit() {
    this.checkScreenWidth();

    this.qualificationStatusFilterYear = this.yearsDropdownDataSource[0].id;
    this.selectedPeriod = DatePeriodModel.ThisYear;
    this.customDateRange = getDateRangeByPeriod(this.selectedPeriod);
    
    const today = new Date();
    this.maxFilterDate = today;

    const maxYearsAgo = 4;
    this.minFilterDate = new Date(today.getFullYear() - maxYearsAgo, 0, 1);

    this.isBrokerPoolTenant = this.tenantContext.isBrokerPoolTenant();
    const role = this.userContext.currentRole;

    switch (role) {
      case 'SuperAdmin':
        this.isSuperAdmin = true;
        break;
      case 'Manager':
        this.isManager = true;
        break;
      case 'Trainer':
        this.isTrainer = true;
        break;
      case 'Employee':
        this.isEmployee = true;
        break;
      case 'Broker':
        this.isBroker = true;
        break;
    }

    this.setDashboardWidgets();
    
    forkJoin({
      trainingGroups: this.trainingGroupService.getTrainingGroupsByPerson(),
      hasAssignedLearningPath:
        this.personService.hasAssignedLearningPath(),
      organisationUnits:
        this.organisationUnitService.getAllOrganisationUnits(),
    }).subscribe(({ 
      trainingGroups, hasAssignedLearningPath, organisationUnits
     }) => {
      this.organisationUnits = organisationUnits;
      const iddGroup = trainingGroups.find((group) => group.trainingType == 0);

      this.setDefaultWidgetLoaded();

      this.loadWidgetData(WIDGET_KEYS.LEARNING_PATHS_PROGRESS_CURRENT);
      this.loadWidgetData(WIDGET_KEYS.LEARNING_PATHS_PROGRESS);

      if (iddGroup) this.isUserInIddGroup = true;
      this.isUserHasLearningPath = hasAssignedLearningPath;

      this.isInitialDataLoaded = true;

      forkJoin({
        allWidgets: this.widgetService.getAllWidgets(),
        personWidgets: this.widgetService.getPersonWidgets(),
      }).subscribe(({ allWidgets, personWidgets }) => {
        this.visibleWidgets = this.mapWidgetsToConfigs(allWidgets);
        this.isVisibleWidgetsLoaded = true;

        if(!personWidgets || personWidgets.length === 0){
          this.personWidgets = this.dashboardWidgets
            .map(dashboardWidget => this.visibleWidgets.find(visibleWidget => visibleWidget.key == dashboardWidget.key))
            .filter(Boolean) as WidgetConfigModel[]; // filter by dashboard widgets
        } else {
          this.personWidgets = this.mapWidgetsToConfigs(personWidgets);
        }

        this.personWidgetKeys = this.personWidgets.map(pw => pw.key);
        this.isPersonWidgetsLoaded = true;
          
        this.loadQualificationStatusData();
        this.loadQualificationStatusPerMonth();

        if (this.canViewLearningRecommendations) {
          this.loadLearningRecommendations();
        }

        if(this.personWidgetKeys.includes(WIDGET_KEYS.PENDING_REQUESTS)) {
          this.loadWidgetData(WIDGET_KEYS.PENDING_REQUESTS);
        }
      });
    });
  }

  widgetHasData: Map<string, boolean> = new Map<string, boolean>();

  getWidgetHasData(widgetKey: string): boolean {
    return this.widgetHasData.get(widgetKey) ?? true; // default: always show
  }

  private setWidgetHasData(widgetKey: string, hasData: boolean): void {
    this.widgetHasData.set(widgetKey, hasData);
  }

  private isDataEmpty(data: any): boolean {
    if (data === null || data === undefined) return true;
    if (Array.isArray(data)) return data.length === 0;
    if (typeof data === 'object') return Object.keys(data).length === 0;
    return false;
  }


  loadLearningRecommendations() {
    this.isLearningRecommendationsLoaded = false;

    this.dashboardService.getLearningRecommendations()
      .subscribe((data) => {
        this.widgetsDataService.setLearningRecommendations(data);
        this.isLearningRecommendationsLoaded = true;
      });
  }

  loadQualificationStatusPerMonth() {
    this.loadWidgetData(WIDGET_KEYS.TREND_TO_TARGET_IDD_DATA);
  }

  loadQualificationStatusData() {
    const nonQualificationStatusDataKeys = [
      WIDGET_KEYS.REPORTING, 
      WIDGET_KEYS.TREND_TO_TARGET_IDD_DATA, 
      WIDGET_KEYS.LEARNING_PATHS_PROGRESS_CURRENT,
      WIDGET_KEYS.LEARNING_PATHS_PROGRESS,
      WIDGET_KEYS.PENDING_REQUESTS
    ];
    
    this.isDashboardStatisticsLoaded = false;
    this.dashboardWidgets.forEach(widget => {
      if(!nonQualificationStatusDataKeys.includes(widget.key)) {
        this.widgetLoadingStatus.set(widget.key, false);
      }
    });

    const [startDate, endDate] = this.customDateRange as Date[];

    this.personWidgetKeys.forEach(widgetKey => {
      if(!nonQualificationStatusDataKeys.includes(widgetKey)) {
        this.loadWidgetData(widgetKey);
      }
    });

    if (this.isSuperAdmin) {
      this.dashboardService.getDashboardComparisonStatistics(
        startDate, endDate, this.selectedOrganisationUnitIds
      ).subscribe((data) => {
        this.widgetsDataService.setDashboardStatistics(data);
        this.isDashboardStatisticsLoaded = true;
      });
    }
  }

  private loadWidgetData(widgetKey: string) {
    const [startDate, endDate] = this.customDateRange as Date[];

    this.setWidgetLoaded(widgetKey, false);

    switch (widgetKey) {
       case WIDGET_KEYS.TREND_TO_TARGET_IDD_DATA:
         this.dashboardService
          .getQualificationStatusPerMonth(this.qualificationStatusFilterYear!)
            .subscribe((trendToTargetIddData) => {
              this.widgetsDataService.setTrendToTargetIddData(trendToTargetIddData);
              this.setWidgetLoaded(widgetKey, true);
            });
        break;

      case WIDGET_KEYS.TOP_10_RECORDS:
        this.dashboardService.getTop10Records(
          startDate, endDate, this.selectedOrganisationUnitIds
        ).subscribe(data => {
          this.widgetsDataService.setTop10Records(data);
          this.setWidgetLoaded(widgetKey, true);
        });
        break;

      case WIDGET_KEYS.EMPLOYEES_PROGRESS:
        this.dashboardService.getEmployeesProgress(
          startDate, endDate, this.selectedOrganisationUnitIds
        ).subscribe(data => {
          this.widgetsDataService.setEmployeesProgress(data);
          this.setWidgetLoaded(widgetKey, true);
        });
        break;

      case WIDGET_KEYS.CHANNEL_PROGRESS:
        this.dashboardService.getChannelProgress(
          startDate, endDate, this.selectedOrganisationUnitIds
        ).subscribe(data => {
          this.widgetsDataService.setChannelProgressData(data);
          this.setWidgetLoaded(widgetKey, true);
        });
        break;

      case WIDGET_KEYS.LEARNING_PATHS_PROGRESS:
        this.dashboardService.getLearningPathsProgress(this.selectedOrganisationUnitIds)
          .subscribe(data => {
            this.widgetsDataService.setLearningPathsProgress(data);
            this.setWidgetLoaded(widgetKey, true);
          });
        break;

      case WIDGET_KEYS.COMPETENCY_MATRIX_DATA:
        this.dashboardService.getEventCompletedCategories(
          startDate, endDate, this.selectedOrganisationUnitIds
        ).subscribe(data => {
          this.widgetsDataService.setCompetencyMatrixData(data);
          this.setWidgetHasData(widgetKey, !this.isDataEmpty(data));
          this.setWidgetLoaded(widgetKey, true);
        });
        break;

      case WIDGET_KEYS.TRAINING_GROUPS_PROGRESS:
        this.dashboardService.getTrainingGroupsProgress(
          startDate, endDate, this.selectedOrganisationUnitIds
        ).subscribe(data => {
          this.widgetsDataService.setComplianceCoursesProgress(data);
          this.setWidgetLoaded(widgetKey, true);
        });
        break;

      case WIDGET_KEYS.COMPLIANCE_COURSES_PROGRESS:
        this.dashboardService.getComplianceCoursesProgress(
          startDate, endDate, this.selectedOrganisationUnitIds
        ).subscribe(data => {
          this.widgetsDataService.setTrainingGroupsProgress(data);
          this.setWidgetLoaded(widgetKey, true);
        });
        break;

      case WIDGET_KEYS.PERSON_COMPETENCY_MATRIX_DATA:
        this.personService.getEventCompletedCategories(startDate, endDate)
          .subscribe(data => {
            this.widgetsDataService.setPersonCompetencyMatrixData(data);
            this.setWidgetHasData(widgetKey, !this.isDataEmpty(data));
            this.setWidgetLoaded(widgetKey, true);
          });
        break;

      case WIDGET_KEYS.PERSON_PROGRESS:
        this.dashboardService.getPersonProgress(startDate, endDate)
          .subscribe(data => {
            if (data) this.widgetsDataService.setPersonProgress(data);
            this.setWidgetLoaded(widgetKey, true);
          });
        break;

      case WIDGET_KEYS.LEARNING_PATHS_PROGRESS_CURRENT:
        this.personService.getLearningPathsProgress()
          .subscribe(data => {
            this.widgetsDataService.setLearningPathsProgressCurrent(data);
            this.setWidgetLoaded(widgetKey, true);
          });
        break;

      case WIDGET_KEYS.PENDING_REQUESTS:
        this.dashboardService.getPendingRequests()
          .subscribe(data => {
            this.widgetsDataService.setPendingRequestData(data);
            this.setWidgetLoaded(widgetKey, true);
          });
        break;

      case WIDGET_KEYS.REPORTING:
        this.setWidgetLoaded(widgetKey, true);
        break;

      default:
        console.warn(`No loader implemented for widget: ${widgetKey}`);
        break;
    }
  }

  private setDashboardWidgets(){
    this.dashboardWidgets = [
      {
        key: WIDGET_KEYS.TOP_10_RECORDS,
        title: this.translateService.instant('DASHBOARD.TOP-10'),
        component: this.widgetKeyToComponentMap[WIDGET_KEYS.TOP_10_RECORDS],
        visible: () => true,
      },
      {
        key: WIDGET_KEYS.EMPLOYEES_PROGRESS,
        title: this.translateService.instant('DASHBOARD.QUALIFICATION-STATUS'),
        component: this.widgetKeyToComponentMap[WIDGET_KEYS.EMPLOYEES_PROGRESS],
        visible: () => true,
      },
      {
        key: WIDGET_KEYS.CHANNEL_PROGRESS,
        title: this.translateService.instant('DASHBOARD.CHANNEL-TRAINING-PARTICIPANTS'),
        component: this.widgetKeyToComponentMap[WIDGET_KEYS.CHANNEL_PROGRESS],
        visible: () => true,
      },
      {
        key: WIDGET_KEYS.PERSON_PROGRESS,
        title: this.translateService.instant('DASHBOARD.MY-QUALIFICATION-STATUS'),
        component: this.widgetKeyToComponentMap[WIDGET_KEYS.PERSON_PROGRESS],
        visible: () => this.isUserInIddGroup,
      },
      {
        key: WIDGET_KEYS.COMPETENCY_MATRIX_DATA,
        title: this.translateService.instant('DASHBOARD.COMPETENCY-MATRIX'),
        component: this.widgetKeyToComponentMap[WIDGET_KEYS.COMPETENCY_MATRIX_DATA],
        visible: () => true,
      },
      {
        key: WIDGET_KEYS.PERSON_COMPETENCY_MATRIX_DATA,
        title: this.translateService.instant('DASHBOARD.COMPETENCY-MATRIX'),
        component: this.widgetKeyToComponentMap[WIDGET_KEYS.PERSON_COMPETENCY_MATRIX_DATA],
        visible: () => true,
      },
      {
        key: WIDGET_KEYS.TRAINING_GROUPS_PROGRESS,
        title: this.translateService.instant('DASHBOARD.TRAINING-GROUP-QUALIFICATION-STATUS'),
        component: this.widgetKeyToComponentMap[WIDGET_KEYS.TRAINING_GROUPS_PROGRESS],
        visible: () => true,
      },
      {
        key: WIDGET_KEYS.COMPLIANCE_COURSES_PROGRESS,
        title: this.translateService.instant('DASHBOARD.COMPLIANCE-COURSE-QUALIFICATION-STATUS'),
        component: this.widgetKeyToComponentMap[WIDGET_KEYS.COMPLIANCE_COURSES_PROGRESS],
        visible: () => true,
      },
      {
        key: WIDGET_KEYS.LEARNING_PATHS_PROGRESS,
        title: this.translateService.instant('DASHBOARD.LEARNING-PATH-STATUS'),
        component: this.widgetKeyToComponentMap[WIDGET_KEYS.LEARNING_PATHS_PROGRESS],
        visible: () => true,
      },
      {
        key: WIDGET_KEYS.LEARNING_PATHS_PROGRESS_CURRENT,
        title: this.translateService.instant('DASHBOARD.LEARNING-PATH-STATUS'),
        component: this.widgetKeyToComponentMap[WIDGET_KEYS.LEARNING_PATHS_PROGRESS_CURRENT],
        visible: () => this.isUserHasLearningPath,
      },
      {
        key: WIDGET_KEYS.TREND_TO_TARGET_IDD_DATA,
        title: this.translateService.instant('DASHBOARD.TREND-TO-TARGET-IDD'),
        component: this.widgetKeyToComponentMap[WIDGET_KEYS.TREND_TO_TARGET_IDD_DATA],
        visible: () => this.isUserInIddGroup,
      },
      {
        key: WIDGET_KEYS.PENDING_REQUESTS,
        title: this.translateService.instant('DASHBOARD.IMPORTANT-TASKS'),
        component: this.widgetKeyToComponentMap[WIDGET_KEYS.PENDING_REQUESTS],
        visible: () => true,
      },
      {
        key: WIDGET_KEYS.REPORTING,
        title: this.translateService.instant('DASHBOARD.REPORTING.TITLE'),
        component: this.widgetKeyToComponentMap[WIDGET_KEYS.REPORTING],
        visible: () => true,
      }
    ];
  }

  private setDefaultWidgetLoaded(){
    this.dashboardWidgets.forEach(widget => {
      this.widgetLoadingStatus.set(widget.key, false);
    });

    this.setWidgetLoaded(WIDGET_KEYS.REPORTING, true);
  }

  setWidgetLoaded(widgetKey: string, loaded: boolean): void {
    if (this.widgetLoadingStatus.get(widgetKey) !== loaded) {
      this.widgetLoadingStatus.set(widgetKey, loaded);
    }
  }

  private mapWidgetsToConfigs(widgets: WidgetModel[]): WidgetConfigModel[] {
    return widgets
      .map(widget => {
        const key = this.widgetNameToKeyMap[widget.name];
        const widgetConfig = this.dashboardWidgets.find(config => config.key === key && config.visible());
        
        if (!widgetConfig) return null;

        const translationKey: string = this.widgetKeyToTranslationKeyMap[key];

        return {
          ...widgetConfig,
          model: {
            id: widget.id,
            name: this.translateService.instant(`${translationKey}.NAME`),
            description: this.translateService.instant(`${translationKey}.DESCRIPTION`)
          } as WidgetModel|undefined,
          
        } as WidgetConfigModel;
      })
      .filter((x): x is WidgetConfigModel => x !== null);
  }

  getWidgetLoadedStatus(widgetKey: string): boolean {
    return this.widgetLoadingStatus.get(widgetKey) ?? true;
  }

  openMyTrainings(): void {
    this.router.navigate(['my-trainings']);
  }

  openTrainingCatalogue(): void {
    this.router.navigate(['training-catalogue']);
  }

  trackByWidgetId(index: number, widget: any) {
    return widget.key;
  }

  enableEditMode() {
    this.editMode = true;
    this.editingWidgets = [...this.personWidgets]; 

    this.splitWidgetsIntoColumns();
    this.updateGalleryItems();
  }

  save() {
    if(this.editingWidgets.length === 0) {
      this.toastrService.error(this.translateService.instant("WIDGETS.FORM.NO-WIDGETS-SELECTED"));
      return;
    }

    this.editMode = false;
    this.isPersonWidgetsLoaded = false;
    this.closeAddWidgetDrawer();

    var personWidgetOrders: PersonWidgetOrderModel[] = this.editingWidgets.map((widget, index) => ({
      widgetId: widget.model!.id,
      order: index + 1
    }));

    this.widgetService.updatePersonWidgets({ personWidgetOrders: personWidgetOrders})
      .subscribe((personWidgets) => {
        this.personWidgets = this.mapWidgetsToConfigs(personWidgets);
        this.personWidgetKeys = this.personWidgets.map(pw => pw.key);
        this.editingWidgets = [];

        this.isPersonWidgetsLoaded = true;
      });
  }

  cancel() {
    this.editMode = false;
    this.editingWidgets = [];
    this.draggedWidget = null;
    this.closeAddWidgetDrawer();
  }

  private updateGalleryItems() {
    const editingWidgetKeys = this.editingWidgets.map(ew => ew.key);
    this.widgetsGallery = this.visibleWidgets
      .filter(widget => !editingWidgetKeys.includes(widget.key))
      .map((widget) => ({
        ...widget,
        demoComponent: this.widgetKeyToDemoComponentMap[widget.key]
      }));
  }

  private splitWidgetsIntoColumns() {
    this.widgetColumns = [[], []];
    this.editingWidgets.forEach((w, i) => {
      this.widgetColumns[i % 2].push(w);
    });
  }

  private syncPersonWidgetsFromColumns() {
    const col1 = this.widgetColumns[0];
    const col2 = this.widgetColumns[1];
    const maxLen = Math.max(col1.length, col2.length);
    const merged: WidgetConfigModel[] = [];

    for (let i = 0; i < maxLen; i++) {
      if (col1[i]) merged.push(col1[i]);
      if (col2[i]) merged.push(col2[i]);
    }

    this.editingWidgets = merged;
    this.updateGalleryItems();
  }

  draggedWidget: WidgetConfigModel | null = null;

  toggleAddWidgetDrawer() {
    this.isAddWidgetDrawerOpen = !this.isAddWidgetDrawerOpen;
  }

  closeAddWidgetDrawer() {
    this.isAddWidgetDrawerOpen = false;
  }

  onDashboardWidgetDragStart(e: any) {
    e.itemData = e.fromData[e.fromIndex];
    this.draggedWidget = e.itemData;
    this.dragSource = 'dashboard';
  }

  onGalleryWidgetDragStart(e: any) {
    e.itemData = e.fromData[e.fromIndex];
    this.draggedWidget = e.itemData;
    this.dragSource = 'gallery';
  }

  onDashboardWidgetsReorder(e: any, colIndex: number) {
    if (this.dragSource === 'dashboard') {
      const column = this.widgetColumns[colIndex];
      const item = column.splice(e.fromIndex, 1)[0];

      column.splice(e.toIndex, 0, item);
      
      this.widgetColumns[colIndex] = [...column]; 
      this.syncPersonWidgetsFromColumns(); 
    }
  }


  onWidgetsAddToDashboard(e: any, targetColIndex: number) {
    if (!this.draggedWidget) return;

    const isFromGallery = this.dragSource === 'gallery';
    const toCol = this.widgetColumns[targetColIndex];

    if (isFromGallery) {
      const newWidgetInstance = { ...this.draggedWidget };
      toCol.splice(e.toIndex, 0, newWidgetInstance);
      this.widgetColumns[targetColIndex] = [...toCol];
      this.closeAddWidgetDrawer();
      this.loadWidgetData(this.draggedWidget.key)
    } else {
      const fromColIndex = this.widgetColumns.findIndex(col => col.includes(this.draggedWidget!));

      if (fromColIndex !== -1) {
        const fromCol = this.widgetColumns[fromColIndex];

        const widgetIndex = fromCol.indexOf(this.draggedWidget);
        if (widgetIndex !== -1) {
          fromCol.splice(widgetIndex, 1);
          this.widgetColumns[fromColIndex] = [...fromCol];
        }

        toCol.splice(e.toIndex, 0, this.draggedWidget);
        this.widgetColumns[targetColIndex] = [...toCol];
      }
    }

    this.draggedWidget = null;
    this.dragSource = null;
    this.syncPersonWidgetsFromColumns();
  }

  onAddWidgetToGallery(e: any) {
    if (!this.draggedWidget) return;

    if (this.dragSource === 'dashboard') {
      this.editingWidgets = this.editingWidgets.filter(w => w.key !== this.draggedWidget!.key);
      this.updateGalleryItems();
      this.splitWidgetsIntoColumns();
    }

    this.draggedWidget = null;
    this.dragSource = null;
  }

  addWidget(key: string) {
    const addWidget = this.widgetsGallery.find(w => w.key === key);
    this.editingWidgets.push(addWidget!);

    this.updateGalleryItems();
    this.splitWidgetsIntoColumns();
    this.loadWidgetData(key)
    this.closeAddWidgetDrawer();

    this.draggedWidget = null;
    this.dragSource = null;
  }

  removeDashboardColumnWidget(colIndex: number, widgetIndex: number) {
    this.widgetColumns[colIndex].splice(widgetIndex, 1);
    this.syncPersonWidgetsFromColumns();
  }

  onQualificationStatusYearFilterChanged(event: any) {
    this.qualificationStatusFilterYear = event.value;
    this.loadQualificationStatusPerMonth();
  }

  onPeriodDateSelectionChanged(e: any): void {
    const selectedKey: DatePeriodModel = e.addedItems[0].key;
    this.selectedPeriod = selectedKey;

    if (selectedKey === DatePeriodModel.Custom) {
      this.isCustomDatePopupOpen = true;
    } else {
      this.customDateRange = getDateRangeByPeriod(selectedKey);
      this.loadQualificationStatusData();
    }
  }

  onCustomDateRangeChanged(dates: any): void {
    if (dates[0] && dates[1]) {
      this.isCustomDatePopupOpen = false;
      this.customDateRange = dates;
      this.loadQualificationStatusData();
    }
  }

  onPeriodTabClicked(e: any): void {
    const clickedItem = e.itemData;

    if (clickedItem?.key === DatePeriodModel.Custom) {
      this.isCustomDatePopupOpen = true;
    }
  }

  onOrganisationUnitsSelected(e: any) {
    const currentSelectionId = e.itemData.id;
    const isSelection = e.node.selected;

    const currentUnit = this.organisationUnits.find(
      (unit) => unit.id === currentSelectionId
    );

    if (currentUnit) {
      currentUnit.selected = isSelection;
    }

    const setChildrenSelection = (parentId: string, selected: boolean) => {
      this.organisationUnits.forEach((unit) => {
        if (unit.parentId === parentId) {
          unit.selected = selected;
          setChildrenSelection(unit.id, selected);
        }
      });
    };

    setChildrenSelection(currentSelectionId, isSelection);

    const checkAndDeselectParents = (childParentId: string | null) => {
      if (!childParentId) return;

      const siblings = this.organisationUnits.filter(
        (unit) => unit.parentId === childParentId
      );
      const allSelected = siblings.every((s) => s.selected);

      if (!allSelected) {
        const parentUnit = this.organisationUnits.find(
          (unit) => unit.id === childParentId
        );
        if (parentUnit && parentUnit.selected) {
          parentUnit.selected = false;
          checkAndDeselectParents(parentUnit.parentId);
        }
      }
    };

    if (!isSelection) {
      checkAndDeselectParents(currentUnit?.parentId ?? null);
    }

    this.selectedOrganisationUnitIds = this.organisationUnits
      .filter((unit) => unit.selected)
      .map((unit) => unit.id);

    this.organisationUnits = [...this.organisationUnits];
    this.loadQualificationStatusData();
  }
}
